import { useState } from "react";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiList,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  ICONS_WEB,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { AddHomeDrawer } from "../../../components/AddHomeDrawer/AddHomeDrawer";
import { useAccountHomesTabViewModel } from "./useAccountHomesTabViewModel";

/**
 * A tab component to display and manage the list of homes within an account.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.account - The account.
 * @param {Object} props.account - The account id.
 * @returns {JSX.Element} AccountHomesTab component.
 */
export const AccountHomesTab = ({ account, accountId }) => {
  const theme = useTheme();
  const { navigate } = useRouter();
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const { homes, isLoading, setHomes, getHomes } = useAccountHomesTabViewModel({
    accountId,
  });

  const { hasPermission } = usePermissions();

  const handleAddDrawerOpen = () => setIsAddDrawerOpen(true);

  const handleAddDrawerClose = () => setIsAddDrawerOpen(false);

  const sections = [
    {
      fieldset: [
        {
          label: "Address",
          field: "address",
          renderValue: ({ address }) => address.addressLine1,
        },
        {
          label: "City",
          field: "address",
          renderValue: ({ address }) => address.city,
        },
        {
          label: "State",
          field: "address",
          renderValue: ({ address }) => address.regionCode,
        },
        {
          label: "Builder",
          field: "builder",
        },
        {
          label: "Latitude",
          field: "address",
          renderValue: ({ address }) => address.location?.latitude?.toFixed(6),
        },
        {
          label: "Longitude",
          field: "address",
          renderValue: ({ address }) => address.location?.longitude?.toFixed(6),
        },
        {
          label: "Zip",
          field: "address",
          renderValue: ({ address }) => address.postalCode,
        },
      ],
    },
  ];

  return (
    <MiBox height="100%">
      <MiBuildPageHeader
        title="Homes"
        titleIcon={
          <MiIcon
            path={mdi[ICONS_WEB.HOME]}
            size={1}
            color={theme.palette.primary.main}
          />
        }
        loading={isLoading}
        actionComponent={
          hasPermission(PERMISSIONS.HOME.CREATE) && (
            <MiButton
              title="Home"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{
                path: mdi[ICONS_WEB.PLUS],
              }}
              onClick={handleAddDrawerOpen}
              data-testid="add-home-button"
            />
          )
        }
      />
      <AddHomeDrawer
        open={isAddDrawerOpen}
        account={account}
        onClose={handleAddDrawerClose}
        onCreate={(home) => navigate(`/homes/${home.id}/details`)}
      />
      <MiList
        viewType="card"
        cardProps={{
          sections,
          onClick: (item) => {
            navigate(`/homes/${item.id}/dashboard`);
          },
        }}
        data={homes}
        loading={isLoading}
        setData={setHomes}
        dataProvider={getHomes}
        paginationMode={PAGINATION.CLIENT}
        emptyListProps={{
          text: "No Homes found",
        }}
      />
    </MiBox>
  );
};
